import {
  areTemplateVariablesFilled,
  extractTemplateVariableIndexes,
  isAuthenticationTemplate,
  parseTemplateComponents,
} from "./templateVariables";
import { validateTemplateForMetaSubmit } from "./templateSubmitValidation";

export const HEADER_FORMATS = ["NONE", "TEXT", "IMAGE", "VIDEO", "DOCUMENT"];

export const BUTTON_TYPES = {
  QUICK_REPLY: "QUICK_REPLY",
  URL: "URL",
  PHONE_NUMBER: "PHONE_NUMBER",
};

export const TEMPLATE_LIMITS = {
  header: 60,
  body: 1024,
  footer: 60,
  buttonText: 25,
  maxQuickReplyButtons: 3,
  maxCtaButtons: 2,
  maxButtons: 3,
};

export const AUTH_CODE_EXPIRATION_OPTIONS = [5, 10, 15, 30, 60];

export const HEADER_MEDIA_ACCEPT = {
  IMAGE: "image/png,image/jpeg,image/jpg",
  VIDEO: "video/mp4",
  DOCUMENT: "application/pdf",
};

export const EMPTY_TEMPLATE_FORM = {
  headerFormat: "TEXT",
  headerText: "",
  headerMediaUrl: "",
  headerMediaFileName: "",
  bodyText: "",
  footerText: "",
  buttons: [],
  variableExamples: {},
  addSecurityRecommendation: true,
  codeExpirationMinutes: 10,
};

const OTP_BUTTON_TEXT = {
  es: "Copiar código",
  en: "Copy code",
  pt_BR: "Copiar código",
};

const AUTH_PREVIEW_COPY = {
  es: {
    body: "*{{code}}* es tu código de verificación.",
    security: "Por tu seguridad, no compartas este código.",
    expires: "Este código expira en {{minutes}} minutos.",
  },
  en: {
    body: "*{{code}}* is your verification code.",
    security: "For your security, do not share this code.",
    expires: "This code expires in {{minutes}} minutes.",
  },
};

const MEDIA_HEADER_FORMATS = new Set(["IMAGE", "VIDEO", "DOCUMENT"]);
const CTA_BUTTON_TYPES = new Set(["URL", "PHONE_NUMBER"]);

export function getOtpButtonText(language) {
  if (OTP_BUTTON_TEXT[language]) {
    return OTP_BUTTON_TEXT[language];
  }
  const base = language?.split("_")[0]?.toLowerCase();
  return OTP_BUTTON_TEXT[base] ?? OTP_BUTTON_TEXT.en;
}

function resolveAuthPreviewCopy(language) {
  if (AUTH_PREVIEW_COPY[language]) {
    return AUTH_PREVIEW_COPY[language];
  }
  const base = language?.split("_")[0]?.toLowerCase();
  return AUTH_PREVIEW_COPY[base] ?? AUTH_PREVIEW_COPY.en;
}

export function isMediaHeaderFormat(format) {
  return MEDIA_HEADER_FORMATS.has(String(format ?? "").toUpperCase());
}

export function getButtonMode(buttons = []) {
  const types = (buttons ?? [])
    .map((button) => String(button?.type ?? "").toUpperCase())
    .filter(Boolean);

  if (types.length === 0) return null;
  if (types.some((type) => CTA_BUTTON_TYPES.has(type))) return "CTA";
  return "QUICK_REPLY";
}

export function isValidHttpsUrl(value) {
  if (typeof value !== "string" || !value.trim()) return false;
  try {
    const url = new URL(value.trim());
    return url.protocol === "https:";
  } catch {
    return false;
  }
}

export function getHeaderMediaAccept(format) {
  return HEADER_MEDIA_ACCEPT[String(format ?? "").toUpperCase()] ?? "";
}

export function isAllowedHeaderMediaFile(format, file) {
  if (!file || !isMediaHeaderFormat(format)) {
    return false;
  }

  const accept = getHeaderMediaAccept(format);
  if (!accept) {
    return false;
  }

  const mime = String(file.type ?? "").split(";")[0]?.trim().toLowerCase();
  const allowed = accept.split(",").map((value) => value.trim().toLowerCase());
  if (mime && allowed.includes(mime)) {
    return true;
  }

  const ext = String(file.name ?? "")
    .split(".")
    .pop()
    ?.toLowerCase();
  const allowedExt = allowed
    .map((value) => value.split("/")[1])
    .filter(Boolean);
  return Boolean(ext && allowedExt.includes(ext));
}

export function hasMediaHeaderSample(form) {
  return isMediaHeaderFormat(form.headerFormat) && isValidHttpsUrl(form.headerMediaUrl);
}

export function canAddButtonType(buttons, type) {
  const mode = getButtonMode(buttons);
  const normalizedType = String(type).toUpperCase();

  if (!mode) {
    return true;
  }

  if (mode === "QUICK_REPLY") {
    return (
      normalizedType === BUTTON_TYPES.QUICK_REPLY &&
      buttons.length < TEMPLATE_LIMITS.maxQuickReplyButtons
    );
  }

  if (mode === "CTA") {
    if (CTA_BUTTON_TYPES.has(normalizedType)) {
      return buttons.length < TEMPLATE_LIMITS.maxCtaButtons;
    }
    return false;
  }

  return false;
}

export function collectFormVariableTexts(form) {
  const texts = [form.bodyText, form.footerText];
  for (const button of form.buttons ?? []) {
    if (typeof button?.url === "string") {
      texts.push(button.url);
    }
  }
  return texts;
}

export function collectFormVariableIndexes(form) {
  const indexes = new Set();
  for (const text of collectFormVariableTexts(form)) {
    extractTemplateVariableIndexes(text ?? "").forEach((index) => indexes.add(index));
  }
  return [...indexes].sort((a, b) => a - b);
}

export function headerHasVariables(headerText) {
  return extractTemplateVariableIndexes(headerText ?? "").length > 0;
}

function parseUrlVariableExample(url, exampleUrl) {
  if (typeof url !== "string" || typeof exampleUrl !== "string") {
    return null;
  }
  const tokenMatch = url.match(/\{\{(\d+)\}\}/);
  if (!tokenMatch) {
    return null;
  }
  const index = Number(tokenMatch[1]);
  const tokenStart = url.indexOf("{{");
  const tokenEnd = url.indexOf("}}") + 2;
  const prefix = url.slice(0, tokenStart);
  const suffix = url.slice(tokenEnd);
  if (!exampleUrl.startsWith(prefix) || !exampleUrl.endsWith(suffix)) {
    return null;
  }
  const value = exampleUrl.slice(prefix.length, exampleUrl.length - suffix.length);
  return value.trim() ? { [index]: value } : null;
}

export function parseVariableExamplesFromComponents(rawComponents) {
  const components = parseTemplateComponents(rawComponents);
  const examples = {};

  for (const component of components) {
    if (!component || typeof component !== "object") continue;
    const type = String(component.type ?? "").toUpperCase();

    if (type === "BODY" && typeof component.text === "string") {
      const indexes = extractTemplateVariableIndexes(component.text);
      const samples = component.example?.body_text?.[0];
      if (Array.isArray(samples)) {
        indexes.forEach((index, sampleIndex) => {
          const value = samples[sampleIndex];
          if (value != null && String(value).trim()) {
            examples[index] = String(value);
          }
        });
      }
    }

    if (type === "BUTTONS" && Array.isArray(component.buttons)) {
      const sampleUrls = component.example?.button_url;
      let dynamicSampleIndex = 0;
      component.buttons.forEach((button) => {
        if (String(button?.type ?? "").toUpperCase() !== "URL") {
          return;
        }
        if (extractTemplateVariableIndexes(button.url ?? "").length === 0) {
          return;
        }
        const parsed = parseUrlVariableExample(
          button.url,
          Array.isArray(sampleUrls) ? sampleUrls[dynamicSampleIndex] : undefined,
        );
        dynamicSampleIndex += 1;
        if (parsed) {
          Object.assign(examples, parsed);
        }
      });
    }
  }

  return examples;
}

export function buildAuthPreviewParts(form, language, code = "123456") {
  const copy = resolveAuthPreviewCopy(language);
  const parts = [{ kind: "body", text: copy.body.replace("{{code}}", code) }];

  if (form.addSecurityRecommendation) {
    parts.push({ kind: "body", text: copy.security });
  }

  if (form.codeExpirationMinutes) {
    parts.push({
      kind: "footer",
      text: copy.expires.replace("{{minutes}}", String(form.codeExpirationMinutes)),
    });
  }

  parts.push({
    kind: "buttons",
    buttons: [{ text: getOtpButtonText(language), type: "OTP" }],
  });

  return parts;
}

export function getNextVariableIndex(...texts) {
  const indexes = new Set();
  texts.flat().forEach((text) => {
    if (typeof text !== "string") return;
    extractTemplateVariableIndexes(text).forEach((index) => indexes.add(index));
  });
  if (indexes.size === 0) return 1;
  return Math.max(...indexes) + 1;
}

export function appendVariable(text, variableIndex) {
  const token = `{{${variableIndex}}}`;
  if (!text || !text.trim()) return token;
  const spacer = text.endsWith(" ") ? "" : " ";
  return `${text}${spacer}${token}`;
}

function parseAuthFieldsFromComponents(components) {
  const form = {
    ...EMPTY_TEMPLATE_FORM,
    buttons: [],
  };

  for (const component of components) {
    if (!component || typeof component !== "object" || Array.isArray(component)) continue;
    const type = String(component.type ?? "").toUpperCase();

    if (type === "BODY") {
      form.addSecurityRecommendation = component.add_security_recommendation === true;
    }

    if (type === "FOOTER" && typeof component.code_expiration_minutes === "number") {
      form.codeExpirationMinutes = component.code_expiration_minutes;
    }

    if (type === "BUTTONS" && Array.isArray(component.buttons)) {
      const otpButton = component.buttons.find((button) => button?.type === "OTP");
      if (otpButton && typeof otpButton.text === "string" && otpButton.text.trim()) {
        form.otpButtonText = otpButton.text.trim();
      }
    }
  }

  return form;
}

function parseHeaderFromComponent(component, form) {
  const format = String(component.format ?? "TEXT").toUpperCase();
  form.headerFormat = format;

  if (format === "TEXT" && typeof component.text === "string") {
    form.headerText = component.text;
    return;
  }

  if (isMediaHeaderFormat(format)) {
    const example = component.example;
    if (Array.isArray(example?.header_url) && example.header_url[0]) {
      form.headerMediaUrl = String(example.header_url[0]);
      form.headerMediaFileName = filenameFromSampleUrl(form.headerMediaUrl);
    }
  }
}

function filenameFromSampleUrl(sampleUrl) {
  if (!sampleUrl) return "";
  try {
    const url = new URL(sampleUrl);
    const disposition = url.searchParams.get("rscd") ?? "";
    const quoted = disposition.match(/filename="([^"]+)"/i);
    if (quoted?.[1]) {
      return quoted[1];
    }
    const parts = url.pathname.split("/").filter(Boolean);
    return parts.length ? decodeURIComponent(parts[parts.length - 1]) : "";
  } catch {
    return "";
  }
}

function parseButtonFromComponent(button) {
  const type = String(button?.type ?? "").toUpperCase();

  if (type === "URL") {
    return {
      type: BUTTON_TYPES.URL,
      text: String(button.text ?? "").trim(),
      url: String(button.url ?? "").trim(),
    };
  }

  if (type === "PHONE_NUMBER") {
    return {
      type: BUTTON_TYPES.PHONE_NUMBER,
      text: String(button.text ?? "").trim(),
      phoneNumber: String(button.phone_number ?? "").trim(),
    };
  }

  if (type === "QUICK_REPLY") {
    return {
      type: BUTTON_TYPES.QUICK_REPLY,
      text: String(button.text ?? "").trim(),
    };
  }

  return null;
}

export function parseComponentsToForm(rawComponents, category) {
  const components = parseTemplateComponents(rawComponents);

  if (category === "AUTHENTICATION" || isAuthenticationTemplate(components)) {
    return parseAuthFieldsFromComponents(components);
  }

  const form = { ...EMPTY_TEMPLATE_FORM, buttons: [] };

  for (const component of components) {
    if (!component || typeof component !== "object" || Array.isArray(component)) continue;
    const type = String(component.type ?? "").toUpperCase();

    if (type === "HEADER") {
      parseHeaderFromComponent(component, form);
    }
    if (type === "BODY" && typeof component.text === "string") {
      form.bodyText = component.text;
    }
    if (type === "FOOTER" && typeof component.text === "string") {
      form.footerText = component.text;
    }
    if (type === "BUTTONS" && Array.isArray(component.buttons)) {
      form.buttons = component.buttons
        .map(parseButtonFromComponent)
        .filter((button) => button && button.text);
    }
  }

  if (!components.some((component) => String(component?.type ?? "").toUpperCase() === "HEADER")) {
    form.headerFormat = "NONE";
  }

  form.variableExamples = parseVariableExamplesFromComponents(components);

  return form;
}

function buildSampleUrl(url, variableExamples) {
  return url.replace(/\{\{(\d+)\}\}/g, (_, rawIndex) => {
    const index = Number(rawIndex);
    const value = variableExamples[index];
    return value != null && String(value).trim() ? String(value) : `{{${index}}}`;
  });
}

export function attachMetaExamplesToComponents(components, variableExamples = {}) {
  return (components ?? []).map((component) => {
    if (!component || typeof component !== "object") {
      return component;
    }

    const type = String(component.type ?? "").toUpperCase();

    if (type === "BODY" && typeof component.text === "string") {
      const indexes = extractTemplateVariableIndexes(component.text);
      if (indexes.length === 0) {
        return component;
      }
      const samples = indexes.map((index) => String(variableExamples[index] ?? "").trim());
      if (!samples.every(Boolean)) {
        return component;
      }
      return {
        ...component,
        example: {
          body_text: [samples],
        },
      };
    }

    if (type === "BUTTONS" && Array.isArray(component.buttons)) {
      const dynamicSamples = [];

      for (const button of component.buttons) {
        if (
          String(button?.type ?? "").toUpperCase() !== "URL" ||
          typeof button?.url !== "string" ||
          extractTemplateVariableIndexes(button.url).length === 0
        ) {
          continue;
        }
        const sampleUrl = buildSampleUrl(button.url, variableExamples);
        if (sampleUrl.includes("{{")) {
          return component;
        }
        dynamicSamples.push(sampleUrl);
      }

      if (dynamicSamples.length === 0) {
        return component;
      }

      return {
        ...component,
        example: { button_url: dynamicSamples },
      };
    }

    return component;
  });
}

function buildMarketingUtilityComponents(form) {
  const components = [];
  const headerFormat = String(form.headerFormat ?? "NONE").toUpperCase();
  const headerText = form.headerText?.trim();
  const bodyText = form.bodyText?.trim();
  const footerText = form.footerText?.trim();
  const buttons = (form.buttons ?? []).filter((button) => button?.text?.trim());

  if (headerFormat === "TEXT" && headerText) {
    components.push({
      type: "HEADER",
      format: "TEXT",
      text: headerText,
    });
  } else if (isMediaHeaderFormat(headerFormat) && form.headerMediaUrl?.trim()) {
    components.push({
      type: "HEADER",
      format: headerFormat,
      example: {
        header_url: [form.headerMediaUrl.trim()],
      },
    });
  }

  if (bodyText) {
    components.push({
      type: "BODY",
      text: bodyText,
    });
  }

  if (footerText) {
    components.push({
      type: "FOOTER",
      text: footerText,
    });
  }

  if (buttons.length > 0) {
    components.push({
      type: "BUTTONS",
      buttons: buttons.map((button) => {
        const type = String(button.type ?? BUTTON_TYPES.QUICK_REPLY).toUpperCase();
        if (type === BUTTON_TYPES.URL) {
          return {
            type: "URL",
            text: button.text.trim(),
            url: button.url?.trim() ?? "",
          };
        }
        if (type === BUTTON_TYPES.PHONE_NUMBER) {
          return {
            type: "PHONE_NUMBER",
            text: button.text.trim(),
            phone_number: button.phoneNumber?.trim() ?? "",
          };
        }
        return {
          type: "QUICK_REPLY",
          text: button.text.trim(),
        };
      }),
    });
  }

  return attachMetaExamplesToComponents(components, form.variableExamples ?? {});
}

export function buildComponentsFromForm(form, category, language = "es") {
  if (category === "AUTHENTICATION") {
    const components = [];
    const body = { type: "BODY" };

    if (form.addSecurityRecommendation) {
      body.add_security_recommendation = true;
    }
    components.push(body);

    const expiration = Number(form.codeExpirationMinutes);
    if (Number.isFinite(expiration) && expiration >= 1 && expiration <= 90) {
      components.push({
        type: "FOOTER",
        code_expiration_minutes: expiration,
      });
    }

    components.push({
      type: "BUTTONS",
      buttons: [
        {
          type: "OTP",
          otp_type: "COPY_CODE",
          text: form.otpButtonText?.trim() || getOtpButtonText(language),
        },
      ],
    });

    return components;
  }

  return buildMarketingUtilityComponents(form);
}

function isValidButton(button) {
  const text = button?.text?.trim();
  if (!text) return false;

  const type = String(button.type ?? BUTTON_TYPES.QUICK_REPLY).toUpperCase();
  if (type === BUTTON_TYPES.URL) {
    return isValidHttpsUrl(button.url);
  }
  if (type === BUTTON_TYPES.PHONE_NUMBER) {
    return /^\+?[0-9]{8,15}$/.test(String(button.phoneNumber ?? "").trim());
  }
  return true;
}

export function isTemplateFormValid(form, category, language = "es") {
  if (category === "AUTHENTICATION") {
    const expiration = Number(form.codeExpirationMinutes);
    return Number.isFinite(expiration) && expiration >= 1 && expiration <= 90;
  }

  if (!form.bodyText?.trim()) {
    return false;
  }

  if (headerHasVariables(form.headerText)) {
    return false;
  }

  const variableIndexes = collectFormVariableIndexes(form);
  if (!areTemplateVariablesFilled(variableIndexes, form.variableExamples ?? {})) {
    return false;
  }

  const submitIssues = validateTemplateForMetaSubmit(
    buildMarketingUtilityComponents(form),
  );
  if (submitIssues.length > 0) {
    return false;
  }

  if (!hasMediaHeaderSample(form) && isMediaHeaderFormat(form.headerFormat)) {
    return false;
  }

  const buttons = form.buttons ?? [];
  const mode = getButtonMode(buttons);
  if (mode === "QUICK_REPLY" && buttons.length > TEMPLATE_LIMITS.maxQuickReplyButtons) {
    return false;
  }
  if (mode === "CTA" && buttons.length > TEMPLATE_LIMITS.maxCtaButtons) {
    return false;
  }

  return buttons.every(isValidButton);
}
